import { useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { Alert, Badge, Button, InlineLoader } from '../../components/ui'
import { keys } from '../../api/keys'
import { fetchChannelAccount } from '../../api/channel'
import { fetchThreads } from '../../api/threads'
import type { ThreadSummary } from '../../api/threads'
import { t } from '../../i18n'
import type { MessageKey } from '../../i18n'
import { oneLine, shortTime } from '../../format'
import { directionLabel, threadModeLabel } from '../../labels'
import { filterQuery } from './InboxLayout'
import type { InboxFilter } from './InboxLayout'

const PAGE = 50

const TABS: ReadonlyArray<{ readonly id: InboxFilter; readonly labelKey: MessageKey }> = [
  { id: 'all', labelKey: 'inbox.filter.all' },
  { id: 'unread', labelKey: 'inbox.filter.unread' },
  { id: 'handoff', labelKey: 'inbox.filter.handoff' },
  { id: 'paused', labelKey: 'inbox.filter.paused' },
]

export function ThreadList({
  filter,
  onFilterChange,
  selected,
}: {
  readonly filter: InboxFilter
  readonly onFilterChange: (filter: InboxFilter) => void
  readonly selected: string | null
}) {
  const [limit, setLimit] = useState(PAGE)
  const [search, setSearch] = useState('')
  const query = { ...filterQuery(filter), query: search.trim(), limit }

  const threads = useQuery({
    queryKey: keys.threads(query),
    queryFn: ({ signal }) => fetchThreads(query, signal),
    // The list is polled by the event stream, so the page must not blank out
    // between filters: a list that flashes empty on every keystroke is a list
    // nobody trusts to be complete.
    placeholderData: keepPreviousData,
  })

  return (
    <>
      {/*
        The inbox had no way to reach one client but scrolling, and the list is
        paged — so a conversation from three weeks ago was found by eye or not at
        all. The server does the narrowing, because filtering a page of fifty
        after it arrives finds four of them and calls it the answer.
      */}
      <input
        className="field__input thread-list__search"
        value={search}
        placeholder={t('inbox.searchPlaceholder')}
        aria-label={t('inbox.searchPlaceholder')}
        onChange={(event) => {
          setSearch(event.target.value)
          setLimit(PAGE)
        }}
      />

      <div className="tabs" role="tablist" aria-label={t('inbox.title')}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === filter}
            className={tab.id === filter ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
            onClick={() => {
              onFilterChange(tab.id)
              setLimit(PAGE)
            }}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {threads.isError ? (
        <div className="inbox__message">
          <Alert tone="error">{t('inbox.failed')}</Alert>
          <Button
            variant="secondary"
            onClick={() => {
              void threads.refetch()
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {threads.isPending ? (
        <div className="inbox__message">
          <InlineLoader />
        </div>
      ) : null}

      {threads.data !== undefined ? (
        <>
          <ol className="thread-list">
            {threads.data.threads.map((thread) => (
              <ThreadRow key={thread.id} thread={thread} selected={thread.id === selected} />
            ))}
          </ol>

          {threads.data.threads.length === 0 ? (
            <p className="inbox__message inbox__message--quiet">
              {filter === 'all' ? <NothingYet /> : t('inbox.emptyFiltered')}
            </p>
          ) : null}

          <div className="inbox__footer">
            <span className="inbox__counter">
              {t('inbox.counter', { shown: threads.data.threads.length, total: threads.data.total })}
            </span>
            {threads.data.threads.length < threads.data.total ? (
              <Button variant="ghost" onClick={() => setLimit((current) => current + PAGE)}>
                {t('common.showMore')}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  )
}

/**
 * One line of the inbox.
 *
 * The quiet ones stay quiet. What earns emphasis is a conversation where the
 * client wrote last — nobody has answered them yet — and a conversation that has
 * been handed to a person. Everything else is grey on purpose: a list where
 * every row is marked is a list with no marks.
 */
function ThreadRow({ thread, selected }: { readonly thread: ThreadSummary; readonly selected: boolean }) {
  const waiting = thread.lastMessage?.direction === 'client_inbound'
  const classes = ['thread-row']
  if (selected) classes.push('thread-row--selected')
  if (waiting) classes.push('thread-row--waiting')

  return (
    <li>
      <NavLink to={`/inbox/${thread.id}`} className={classes.join(' ')}>
        <div className="thread-row__top">
          <span className="thread-row__name">{thread.displayName}</span>
          <span className="thread-row__time">{shortTime(thread.lastMessage?.at ?? thread.lastInboundAt)}</span>
        </div>

        <div className="thread-row__preview">
          {thread.lastMessage === undefined ? (
            <span className="thread-row__quiet">{t('inbox.noMessages')}</span>
          ) : (
            <>
              <span className="thread-row__who">{directionLabel(thread.lastMessage.direction)}:</span>{' '}
              {oneLine(thread.lastMessage.text)}
            </>
          )}
        </div>

        <div className="thread-row__badges">
          {thread.unreadCount > 0 ? <span className="unread">{thread.unreadCount}</span> : null}
          {thread.pendingDrafts !== undefined && thread.pendingDrafts > 0 ? (
            <Badge tone="accent">{t('inbox.draftWaiting')}</Badge>
          ) : null}
          {/*
            Work open against this conversation — a handoff, a refund, a lost
            session. The server has counted it all along and the panel dropped
            it, so the one signal saying "this one needs you" never reached the
            row it was about.
          */}
          {thread.openTasks !== undefined && thread.openTasks > 0 ? (
            <Badge tone="warning">{t('inbox.tasksWaiting', { count: thread.openTasks })}</Badge>
          ) : null}
          {thread.mode === 'handoff_pending' ? (
            <Badge tone="warning">{threadModeLabel(thread.mode)}</Badge>
          ) : null}
          {thread.mode !== 'handoff_pending' && thread.mode !== 'ai_active' ? (
            <Badge tone="muted">{threadModeLabel(thread.mode)}</Badge>
          ) : null}
          {waiting ? <span className="thread-row__waiting">{t('inbox.waitingReply')}</span> : null}
        </div>
      </NavLink>
    </li>
  )
}

/**
 * An empty inbox, and why it is empty.
 *
 * On a fresh install this is the first screen anybody sees, and «Диалогов нет.»
 * is true and useless: nothing on it says that no Telegram account is connected,
 * which is the actual reason and is fixed two clicks away. The panel already
 * knows how to say "this is off, here is where you fix it" — the red line about
 * sending, five lines above this list — and this is the same sentence for the
 * step before it.
 *
 * It asks the server rather than guessing: a connected account with a quiet day
 * is also an empty inbox, and telling that owner to connect an account would be
 * worse than saying nothing.
 */
function NothingYet() {
  const channel = useQuery({
    queryKey: ['channel-account'],
    queryFn: ({ signal }) => fetchChannelAccount(signal),
    // Once per screen. It changes when somebody signs an account in, and that
    // person is on the settings screen, not this one.
    staleTime: 60_000,
  })

  // Only when it is certain. A connected account having a quiet day is also an
  // empty inbox, and telling that owner to connect an account would be worse
  // than saying nothing; so is guessing while the answer is still loading.
  if (channel.data === undefined || channel.data.phase === 'connected') {
    return <>{t('inbox.empty')}</>
  }

  return (
    <span className="inbox__emptyReason">
      {t('inbox.emptyNoAccount')} <Link to="/settings">{t('inbox.emptyConnect')}</Link>
    </span>
  )
}
